import { useState, type ReactNode } from "react";
import { Link, useRoute } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Building2, Loader2, MapPin, Zap } from "lucide-react";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { InlineEdit } from "@/components/InlineEdit";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { AddContactModal } from "@/components/add/AddContactModal";
import { CapitalProviderEnrichReview } from "@/components/CapitalProviderEnrichReview";
import { ActivityTimeline } from "@/components/activity/ActivityTimeline";
import { EntityRemindersButton } from "@/components/reminders/EntityRemindersButton";
import { useCurrentUserTimezone } from "@/hooks/useCurrentUserTimezone";
import { useCompanyField } from "@/hooks/useCompanyField";
import { useCompanySummary } from "@/hooks/useRecordSummary";
import { RecordFrame, RecordPanel } from "@/components/record/RecordFrame";
import { RecordHeader, RecordChip, type SublineItem } from "@/components/record/RecordHeader";
import { FactsStrip } from "@/components/record/FactsStrip";
import { ActionBar } from "@/components/record/ActionBar";
import { BriefPanel } from "@/components/record/BriefPanel";
import { EditableTextPanel } from "@/components/record/EditableTextPanel";
import { CompanyRail } from "@/components/record/CompanyRail";
import { CompanyReach } from "@/components/record/CompanyReach";
import { companyLinkItems } from "@/components/record/LinkField";
import { CompanyNameEdit } from "@/components/record/CompanyNameEdit";
import { CompanyMoreMenu } from "@/components/record/CompanyMoreMenu";
import { ArchiveCompanyDialog } from "@/components/record/ArchiveCompanyDialog";
import { useRecordCompose } from "@/components/record/useRecordCompose";
import { capitalProviderFacts } from "@/components/record/facts/companyFacts";
import { fullDay, localYmd, primaryContact, sourceLabel, websiteHost } from "@/components/record/recordFormat";
import type { CompanyData } from "./company/companyTypes";
import { LendingCriteriaPanel } from "./company/capital/LendingCriteriaPanel";
import { useCapitalProfile } from "./company/capital/useCapitalProfile";

export default function CapitalProviderDetail() {
  const [, params] = useRoute("/capital-providers/:id");
  const id = params?.id;
  const queryClient = useQueryClient();
  const userTz = useCurrentUserTimezone();
  const { saveFieldAsync } = useCompanyField(id);
  const summary = useCompanySummary(id);
  const capital = useCapitalProfile(id);
  const { compose, setCompose, openLog, nextStepOpen, setNextStepOpen } = useRecordCompose();
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [enrichPhase, setEnrichPhase] = useState<"idle" | "website" | "apollo" | "review" | "done">("idle");
  const [enrichResult, setEnrichResult] = useState<any>(null);
  const [enrichNoWebsiteConfirm, setEnrichNoWebsiteConfirm] = useState(false);

  const { data: company, isLoading } = useQuery<CompanyData>({
    queryKey: ["company", id],
    queryFn: async () => {
      const r = await fetch(`/api/companies/${id}`, { credentials: "include" });
      if (!r.ok) throw new Error("Failed to fetch");
      return r.json();
    },
    enabled: !!id,
  });

  // No website means an Apollo search by name only, so ask first.
  const runEnrich = async () => {
    const d = websiteHost(company?.website);
    if (!d) { setEnrichNoWebsiteConfirm(true); return; }
    await executeEnrich(d);
  };

  const executeEnrich = async (d: string | null) => {
    setEnrichPhase("website");
    try {
      const res = await fetch(`/api/companies/${id}/enrich-combined`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain: d || null }),
      });
      const data = await res.json();
      setEnrichResult(data);
      if (!data.website && !data.apollo && !data.websiteError) {
        toast("No enrichment data found");
        setEnrichPhase("idle");
        return;
      }
      setEnrichPhase("review");
    } catch {
      toast.error("Enrichment failed");
      setEnrichPhase("idle");
    }
  };

  if (isLoading) {
    return (
      <AppLayout title="Loading...">
        <div className="flex items-center justify-center py-16"><Loader2 className="h-10 w-10 animate-spin text-primary-text" /></div>
      </AppLayout>
    );
  }
  if (!company) {
    return (
      <AppLayout title="Not Found">
        <div className="flex flex-col items-center justify-center py-16">
          <Building2 className="mb-4 h-16 w-16 text-muted-foreground/30" />
          <h2 className="mb-2 text-xl font-semibold">Capital provider not found</h2>
          <Link href="/capital-providers"><Button className="mt-4 gap-2"><ArrowLeft className="h-4 w-4" />Back to Capital Providers</Button></Link>
        </div>
      </AppLayout>
    );
  }

  const people = company.contacts ?? [];
  const primary = primaryContact(people);
  const today = localYmd(new Date());
  const enriching = enrichPhase === "website" || enrichPhase === "apollo";

  const subline: SublineItem[] = [
    { icon: <MapPin />, content: <InlineEdit label="Headquarters" value={company.headquarters} placeholder="City, State" addLabel="Add HQ" onSave={(v) => saveFieldAsync("headquarters", v.trim() || null)} /> },
    ...companyLinkItems({ website: company.website, linkedinUrl: company.linkedinUrl, onSave: (field, v) => saveFieldAsync(field, v) }),
  ];

  const header = (
    <RecordHeader
      crumb={{ label: "Capital Providers", href: "/capital-providers" }}
      current={company.name}
      name={<CompanyNameEdit value={company.name} onSave={(t) => saveFieldAsync("name", t)} />}
      chips={<>
        <RecordChip tone="category">Capital provider</RecordChip>
        {company.dualRoleBuyerCp && <RecordChip>Also buyer</RecordChip>}
        {people.some((p) => p.engaged) && <RecordChip tone="good">Spoken with</RecordChip>}
      </>}
      subline={subline}
      reach={<CompanyReach people={people} mainPhone={company.enrichmentData?.phone} />}
      actions={
        <ActionBar onLog={openLog} email={primary?.email} onNextStep={() => setNextStepOpen(true)}>
          <EntityRemindersButton entityType="company" entityId={company.id} entityName={company.name} userTimezone={userTz} />
          <Button size="sm" variant="outline" className="gap-1.5" onClick={runEnrich} disabled={enriching}>
            {enriching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
            {enriching ? "Enriching…" : "Enrich"}
          </Button>
          <CompanyMoreMenu company={company} onArchive={() => setArchiveOpen(true)} />
        </ActionBar>
      }
    />
  );

  const work = (
    <>
      <BriefPanel
        summary={summary.data}
        loading={summary.isLoading}
        error={summary.isError}
        onRetry={() => summary.refetch()}
        openLabel={`Open on ${company.name}`}
        target={{ companyId: company.id }}
        nextStepOpen={nextStepOpen}
        onNextStepOpenChange={setNextStepOpen}
      />
      <LendingCriteriaPanel capital={capital} />
      <EditableTextPanel
        title="About"
        value={company.description}
        onSave={(v) => saveFieldAsync("description", v.trim() || null)}
        emptyText="No description yet. Enrich to pull it from their website, or add it here."
      />
      <RecordPanel id="record-activity">
        <ActivityTimeline
          scope={{ kind: "company", companyId: company.id, companyName: company.name, contacts: people }}
          compose={compose}
          onComposeChange={setCompose}
        />
      </RecordPanel>
    </>
  );

  const details: [string, ReactNode][] = [
    ["Added", fullDay(company.createdAt)],
    ["Source", sourceLabel(company.creationSource)],
    ["Added by", company.creator?.name ?? "Unknown"],
  ];

  return (
    <AppLayout title={company.name}>
      <RecordFrame
        header={header}
        facts={summary.data
          ? <FactsStrip facts={capitalProviderFacts(summary.data, today, { lastActivityAt: company.lastActivityAt ?? null, profile: capital.profile ?? null })} />
          : summary.isLoading ? <div className="h-[58px] animate-pulse rounded-lg border bg-card" /> : null}
        work={work}
        rail={
          <CompanyRail
            company={company}
            summary={summary}
            details={details}
            onSaveNotes={(v) => saveFieldAsync("notes", v.trim() || null)}
            onAddPerson={() => setAddPersonOpen(true)}
          />
        }
      />
      <AddContactModal open={addPersonOpen} onOpenChange={setAddPersonOpen} preselectedCompanyId={company.id} preselectedCompanyName={company.name} />
      <ArchiveCompanyDialog company={company} open={archiveOpen} onOpenChange={setArchiveOpen} />
      <CapitalProviderEnrichReview
        open={enrichPhase === "review"}
        onOpenChange={(open) => { if (!open) setEnrichPhase("idle"); }}
        company={{ id: company.id, name: company.name }}
        enrichResult={enrichResult || {}}
        onApplied={() => {
          setEnrichPhase("done");
          setEnrichResult(null);
          queryClient.invalidateQueries({ queryKey: ["company", id] });
          queryClient.invalidateQueries({ queryKey: ["capital-profile", id] });
          toast.success("Enrichment applied");
        }}
      />
      <ConfirmDialog
        open={enrichNoWebsiteConfirm}
        onOpenChange={setEnrichNoWebsiteConfirm}
        title={`Enrich ${company.name} using company name only?`}
        description="No website is set for this record, so enrichment will search Apollo by company name only. Results may be less accurate than a website-based lookup."
        confirmLabel="Run Apollo enrichment"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={async () => { setEnrichNoWebsiteConfirm(false); await executeEnrich(null); }}
      />
    </AppLayout>
  );
}
